// frontend/src/modules/notifications/components/NotificationCenter.tsx
//
// Bell button + dropdown popover for the dashboard header. Self-contained:
// owns its open state, click-outside, unread-count polling (30s, pauses on
// hidden via usePoll), and the notification list. Drop into the header where
// the old decorative bell button lived.

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, CheckCheck } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { usePoll } from '../../monitoring/usePoll';
import { formatDateTime } from '../../../design-system/format';
import {
  listNotifications, unreadCount, markRead, markAllRead,
} from '../api';
import type { Notification } from '../types';

/** Compact relative time (falls back to absolute for older items). */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDateTime(iso);
}

export function NotificationCenter() {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [count, setCount] = React.useState(0);
  const [items, setItems] = React.useState<Notification[]>([]);
  const [loading, setLoading] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  // Poll the unread count every 30s (usePoll pauses while the tab is hidden).
  const refreshCount = React.useCallback(async () => {
    try {
      const { count } = await unreadCount();
      setCount(count);
    } catch {
      /* swallow — transient errors shouldn't surface in the header */
    }
  }, []);
  usePoll(refreshCount, 30_000, true);

  const loadList = React.useCallback(async () => {
    setLoading(true);
    try {
      const { notifications } = await listNotifications({ limit: 30 });
      setItems(notifications);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load the list whenever the popover opens.
  React.useEffect(() => {
    if (open) void loadList();
  }, [open, loadList]);

  // Click-outside closes.
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const onItemClick = async (n: Notification) => {
    if (!n.read_at) {
      // Optimistic: mark read locally + decrement badge, then persist.
      setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, read_at: new Date().toISOString() } : i)));
      setCount((c) => Math.max(0, c - 1));
      markRead(n.id).catch(() => { /* best-effort */ });
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const onMarkAll = async () => {
    setItems((prev) => prev.map((i) => (i.read_at ? i : { ...i, read_at: new Date().toISOString() })));
    setCount(0);
    try { await markAllRead(); } catch { /* best-effort */ }
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        aria-label="Notifications"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'relative inline-flex h-9 w-9 items-center justify-center rounded-full',
          'bg-white/80 dark:bg-white/[0.08] backdrop-blur-md shadow-pill',
          'text-ink dark:text-white',
          'hover:bg-white dark:hover:bg-white/[0.14] transition-colors',
        )}
      >
        <Bell className="h-4 w-4" />
        {count > 0 && (
          <span
            aria-hidden
            className={cn(
              'absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full',
              'bg-coral text-white text-[9px] font-bold leading-none',
              'inline-flex items-center justify-center ring-2 ring-white dark:ring-[#0E0E14]',
            )}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.14 }}
            className={cn(
              'absolute right-0 top-11 w-[22rem] max-w-[calc(100vw-2rem)] rounded-2xl z-50 origin-top-right overflow-hidden',
              'bg-white/95 dark:bg-[#15151B]/95 backdrop-blur-xl',
              'ring-1 ring-ink/10 dark:ring-white/10',
              'shadow-[0_18px_60px_-18px_rgba(15,15,18,0.35)]',
            )}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-ink/8 dark:border-white/10">
              <p className="text-[13px] font-semibold text-ink dark:text-white">Notifications</p>
              {items.some((i) => !i.read_at) && (
                <button
                  type="button"
                  onClick={onMarkAll}
                  className={cn(
                    'inline-flex items-center gap-1 text-[11.5px] font-medium',
                    'text-ink/60 dark:text-white/60 hover:text-coral dark:hover:text-coral transition-colors',
                  )}
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Mark all read
                </button>
              )}
            </div>

            <div className="max-h-[24rem] overflow-y-auto">
              {loading && items.length === 0 ? (
                <p className="px-4 py-8 text-center text-[12.5px] text-ink/50 dark:text-white/45">
                  Loading…
                </p>
              ) : items.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <Bell className="h-6 w-6 mx-auto text-ink/25 dark:text-white/25" />
                  <p className="mt-2 text-[12.5px] text-ink/50 dark:text-white/45">
                    You're all caught up.
                  </p>
                </div>
              ) : (
                <ul>
                  {items.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => onItemClick(n)}
                        className={cn(
                          'w-full text-left flex gap-3 px-4 py-3',
                          'hover:bg-ink/[0.04] dark:hover:bg-white/[0.05] transition-colors',
                          'border-b border-ink/5 dark:border-white/[0.06] last:border-b-0',
                        )}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            'mt-1.5 size-2 rounded-full shrink-0',
                            n.read_at ? 'bg-transparent' : 'bg-coral',
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className={cn(
                            'block text-[12.5px] truncate',
                            n.read_at ? 'font-medium text-ink/75 dark:text-white/70' : 'font-semibold text-ink dark:text-white',
                          )}>
                            {n.title}
                          </span>
                          {n.body && (
                            <span className="block text-[11.5px] text-ink/55 dark:text-white/50 truncate mt-0.5">
                              {n.body}
                            </span>
                          )}
                          <span className="block text-[10.5px] text-ink/40 dark:text-white/35 mt-1">
                            {relativeTime(n.created_at)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="border-t border-ink/8 dark:border-white/10">
              <button
                type="button"
                onClick={() => { setOpen(false); navigate('/dashboard/notifications'); }}
                className={cn(
                  'w-full px-4 py-2.5 text-[12px] font-semibold text-center transition-colors',
                  'text-ink/70 dark:text-white/70 hover:text-coral dark:hover:text-coral',
                )}
              >
                View all notifications
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
